import React, { useState, useEffect, useContext } from 'react'
import { Button, FormControl, Modal } from 'react-bootstrap'

import authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

// import Modal from 'plugin-codaf-risk-ext-bowtie/BowtieManagment/components/Bowtie/components/BarrierDetailsModal/BarrierDetailsModal'
import { ImportDataModal, IncompleteExportConfimationModal, RoundedModal } from 'plugin-codaf-risk-ext-bowtie/generic'
import { DraExportData } from 'plugin-codaf-risk-ext-bowtie/services/export'
import { CustomList, RemoveConfirmationModal } from 'plugin-generic-components/shared/components/components'

import { FilteredSearch } from '../../../generic'
import { deleteDiagram, getOperations, importRiskDiagramData, listDiagrams } from '../../../services'
import { IDiagram, IOperation } from '../../../types'
import { PAGINATION_SIZE } from '../../../utils/constants'
import { BowtieSettingsRouteContext, IBowtieSettingsRouteContext, IQueryParamsList } from '../../BowtieSettingWrapper'
import { AddDiagramModal } from './components'
import { DiagramsListContainer, ImportBody } from './styles'

export interface IDiagramListItem {
    id: number
    hazard: string
    topEvent: string
    operation: string
}

export const RiskDiagramList: React.FC = () => {
    const [request, setRequest] = useState<{ total: number; totalPages: number; data: IDiagram[] }>()
    const [operations, setOperations] = useState<IOperation[]>([])

    const [showAddDiagramModal, setShowAddDiagramModal] = useState(false)
    const [showRemoveDiagramModal, setShowRemoveDiagramModal] = useState(false)
    const [showImportModal, setShowImportModal] = useState(false)
    const [showIncompleteExportConfimationModal, setShowIncompleteExportConfimationModal] = useState(false)
    const [tempDiagram, setTempDiagram] = useState<IDiagramListItem>(undefined)
    const [loading, setLoading] = useState<boolean>(false)
    const [exportImportLoading, setExportImportLoading] = useState<boolean>(false)
    const [files, setFiles] = useState<FileList>(null)

    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN
    const bowtieManager = authorization.Permission.BOWTIE_MANAGER

    const { params, setParams } = useContext<IBowtieSettingsRouteContext<IQueryParamsList>>(BowtieSettingsRouteContext)

    const handleCloseProtocol = (): void => {
        setShowImportModal(false)
        setFiles(null)
    }

    const handleGetList = async (): Promise<void> => {
        try {
            setLoading(true)
            const diagrams = await listDiagrams({
                direction: params?.direction,
                page: params?.page,
                perPage: params?.perPage,
                keyword: params?.keyword?.toString(),
                operation: params?.filterBy,
                orderBy: params?.orderBy
            })

            setRequest(diagrams)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on list bowties.'))
        } finally {
            setLoading(false)
        }
    }

    const submitImportData = async (file): Promise<void> => {
        try {
            if (file == null) {
                return globalNotification.error(i18n('Please select a file.'))
            }
            setExportImportLoading(true)
            await importRiskDiagramData(file)
            globalNotification.success(i18n('Data imported successfully.'))
            await handleGetList()
            handleCloseProtocol()
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error occurred on importing Risk Diagram data.'))
            console.log(error)
        } finally {
            setExportImportLoading(false)
        }
    }

    const handleExportData = async (): Promise<void> => {
        try {
            setExportImportLoading(true)
            await DraExportData({ entityType: 'risk-diagram', acceptEmptyArguments: 'false' })
        } catch (error) {
            if (error.message.toLowerCase().includes('there is at least')) {
                setShowIncompleteExportConfimationModal(true)
                globalNotification.error(i18n('Confirm incomplete export'))
            } else {
                globalNotification.error(error.message ?? i18n('An error occurred on exporting Risk Diagram data.'))
                console.log(error)
            }
        } finally {
            setExportImportLoading(false)
        }
    }

    const handleSearch = async (keyword?: string, filterBy?: number): Promise<void> => {
        if (filterBy) {
            setParams({ ...params, filterBy: filterBy < 1 ? undefined : operations[filterBy - 1].id, keyword })
        } else {
            setParams({ ...params, keyword })
        }
    }

    const handleSortClick = (orderBy: string, direction: 'ASC' | 'DESC'): void => {
        setParams({ ...params, direction, orderBy })
    }

    const handlePerPageChange = (): void => {
        setParams({ ...params, perPage: Math.min((params.perPage += PAGINATION_SIZE), request?.total) })
    }

    const handleNewDiagram = (): void => {
        setShowAddDiagramModal(true)
    }

    const handleCopyDiagram = (k: IDiagramListItem): void => {
        setTempDiagram(k)
        setShowAddDiagramModal(true)
    }

    const handleEditDiagram = (k: IDiagramListItem): void => {
        setParams({ type: 'edit', tab: 'diagrams', id: k.id })
    }

    const handleDeleteDiagram = (k: IDiagramListItem): void => {
        setTempDiagram(k)
        setShowRemoveDiagramModal(true)
    }

    const handleCloseDeleteDiagram = (): void => {
        handleGetList()
        setShowRemoveDiagramModal(false)
        setTempDiagram(undefined)
    }

    const handleCloseAddDiagramModal = (): void => {
        setShowAddDiagramModal(false)
        setTempDiagram(undefined)
    }

    useEffect(() => {
        handleGetList()
    }, [params]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handle = async (): Promise<void> => {
            const opFetch = await getOperations()
            setOperations(opFetch.data)
        }

        handle()
    }, [])

    return (
        <DiagramsListContainer>
            <div className="first-row-container">
                {bowtieAdmin && (
                    <Button
                        className="no-outline-button"
                        bsStyle="primary"
                        onClick={handleNewDiagram}
                        style={{ borderRadius: 3, marginRight: 8 }}
                        id="diagramList-list-button-newDiagram"
                    >
                        <i className="far fa-plus custom-plus" /> {i18n('New Diagram')}
                    </Button>
                )}
                <FilteredSearch
                    selectedFilterOption={operations.findIndex(o => o.id === params.filterBy) + 1}
                    keyword={params.keyword}
                    inputId="diagramList-list-field-searchDiagram"
                    filterOptions={[i18n('All Operations'), ...operations.map(op => op.description)]}
                    onSearch={handleSearch}
                />
                {(bowtieAdmin || bowtieManager) && (
                    <Button
                        className="export-import-button"
                        onClick={() => {
                            handleExportData()
                        }}
                        id="export-data"
                    >
                        {exportImportLoading ? (
                            <i className="fa fa-spinner fa-spin fa-x2" />
                        ) : (
                            <>
                                <i className="fas fa-download" /> {i18n('Export data')}
                            </>
                        )}
                    </Button>
                )}
                {bowtieAdmin && (
                    <Button
                        className="export-import-button"
                        onClick={() => {
                            setShowImportModal(old => !old)
                        }}
                    >
                        {exportImportLoading ? (
                            <i className="fa fa-spinner fa-spin fa-x2" />
                        ) : (
                            <>
                                <i className="fas fa-upload" /> {i18n('Import data')}
                            </>
                        )}
                    </Button>
                )}
            </div>
            {loading ? (
                <div className="loading-spin">
                    <i className="fa fa-spinner fa-spin fa-x2" />
                </div>
            ) : request?.data?.length ? (
                <CustomList
                    label="Diagrams"
                    list={request.data.map(
                        (el): IDiagramListItem => ({
                            id: el?.id ?? undefined,
                            hazard: el?.hazard?.name ?? '',
                            topEvent: el?.topEvent?.name ?? '',
                            operation: el?.hazard?.operation?.description ?? ''
                        })
                    )}
                    columns={[
                        { title: 'ID', sortable: true, listRef: 'id' },
                        { title: 'HAZARD', sortable: true, listRef: 'hazard' },
                        { title: 'TOP EVENT', sortable: true, listRef: 'topEvent' },
                        { title: 'OPERATION', sortable: true, listRef: 'operation' }
                    ]}
                    sortDirection={params.direction}
                    sortField={params.orderBy}
                    onSortClick={handleSortClick}
                    tableProps={{ style: { width: '100%' } }}
                    configOptions={
                        bowtieAdmin
                            ? [
                                  { icon: 'far fa-edit', onClickConfig: handleEditDiagram },
                                  { icon: 'far fa-copy', onClickConfig: handleCopyDiagram },
                                  { icon: 'far fa-trash-alt', onClickConfig: handleDeleteDiagram }
                              ]
                            : [{ icon: 'fas fa-eye', onClickConfig: handleEditDiagram }]
                    }
                    pagination={{
                        type: 'live',
                        totalItems: request.total,
                        showingItems: params.perPage,
                        onPerPageChange: handlePerPageChange
                    }}
                    fixedHeader={true}
                />
            ) : (
                <div className="no-diagram">
                    <i className="far fa-folder-open " />
                    <p>{i18n('There are no diagrams yet')}</p>
                    <a onClick={handleNewDiagram}>{i18n('Create new diagram')}</a>
                </div>
            )}

            <AddDiagramModal
                handleClose={handleCloseAddDiagramModal}
                handleSave={id => setParams({ type: 'edit', tab: 'diagrams', id })}
                show={showAddDiagramModal}
                operations={operations}
                item={tempDiagram}
            />

            <RemoveConfirmationModal
                handleClose={handleCloseDeleteDiagram}
                onDelete={async () => await deleteDiagram(tempDiagram.id)}
                show={showRemoveDiagramModal}
                setShow={setShowRemoveDiagramModal}
                type="diagram"
                removalMessage={`${i18n('Are you sure you want remove the diagram')} ${tempDiagram?.id}?`}
            />
            <ImportDataModal
                handleClose={handleCloseProtocol}
                show={showImportModal}
                submit={submitImportData}
                type={'risk-diagram'}
                files={files}
                setFiles={setFiles}
            />
            <IncompleteExportConfimationModal
                setLoading={setExportImportLoading}
                handleClose={() => setShowIncompleteExportConfimationModal(false)}
                show={showIncompleteExportConfimationModal}
                type={'risk-diagram'}
            />
        </DiagramsListContainer>
    )
}
